import { useState } from 'react';
import { Info } from 'lucide-react';

import { toast } from 'sonner';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Label } from '@/ui/base/label';
import { RadioGroup, RadioGroupItem } from '@/ui/base/radio-group';
import { Textarea } from '@/ui/base/textarea';

import { TIPO_DE_REVISAO_META, type TipoDeRevisao } from './contrato';

interface SolicitarRevisaoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Medição a revisar. Ainda sem uso: o envio só avisa enquanto não houver API de revisão. */
  medicaoId: string;
  competencia: string;
}

const MOTIVO_MINIMO = 10;

/**
 * Pedido de revisão de uma medição — apontamento (quantidade de alunos,
 * faltas, KM) ou multa (glosa/penalidade vinculada à OS), a ser encaminhado à
 * SME.
 *
 * O envio não grava nada: sem API de revisão, "Enviar solicitação" valida o
 * motivo e só mostra o aviso de indisponibilidade (CLAUDE.md, seção
 * "Proibido"). O modal fica aberto com o que foi digitado, como em
 * `financeiro/solicitacoes-sme/NovaSolicitacaoSmeModal.tsx`, e traz um aviso
 * fixo de que nada é registrado — aviso passageiro some, e a captura de tela
 * ficaria parecendo envio. Até 2026-10-02 o texto fixo dizia que a solicitação
 * "é encaminhada para análise da SME", o que não acontece (decisão D-E).
 */
export function SolicitarRevisaoModal({
  open,
  onOpenChange,
  competencia,
}: SolicitarRevisaoModalProps) {
  const [tipo, setTipo] = useState<TipoDeRevisao>('apontamento');
  const [motivo, setMotivo] = useState('');

  const enviar = () => {
    if (motivo.trim().length < MOTIVO_MINIMO) {
      toast.error('Descreva o motivo', { description: `Mínimo de ${MOTIVO_MINIMO} caracteres.` });
      return;
    }
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Solicitar revisão de medição"
      description={`Competência ${competencia}`}
      desktopMaxWidth="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={enviar}>Enviar solicitação</Button>
        </>
      }
    >
      <div className="space-y-4">
        <RadioGroup value={tipo} onValueChange={(valor) => setTipo(valor as TipoDeRevisao)}>
          {(Object.keys(TIPO_DE_REVISAO_META) as TipoDeRevisao[]).map((opcao) => (
            <label
              key={opcao}
              htmlFor={`tipo-${opcao}`}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:bg-muted/40"
            >
              <RadioGroupItem value={opcao} id={`tipo-${opcao}`} className="mt-0.5" />
              <div>
                <p className="text-sm font-medium">{TIPO_DE_REVISAO_META[opcao].rotulo}</p>
                <p className="text-xs text-muted-foreground">
                  {TIPO_DE_REVISAO_META[opcao].descricao}
                </p>
              </div>
            </label>
          ))}
        </RadioGroup>

        <div className="space-y-1.5">
          <Label htmlFor="motivo-revisao">Motivo</Label>
          <Textarea
            id="motivo-revisao"
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            placeholder="Descreva o que deve ser revisado e por quê…"
            rows={4}
          />
        </div>

        <Alert>
          <Info className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            A solicitação de revisão ainda não pode ser enviada por aqui. Você pode preencher e
            conferir o formulário, mas nada é registrado.
          </AlertDescription>
        </Alert>
      </div>
    </ResponsiveModal>
  );
}
